import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase/config";
import { userService } from "./userService";
import { StripeService } from "./stripeService";
import type { UserData } from "../types/user";
import { FINISH_STATUS } from "../components/onboarding/onboardingCopy";
import { safeGetItem } from "../utils/localStorageUtils";

/** Everything the finish step needs from the wizard state. */
export interface OnboardingRegistrationInput {
  uid: string;
  /** Auth email when available, else the typed one. */
  email: string;
  firstName: string;
  username: string;
  dob: string;
  sex: "female" | "male" | null;
  selectedSportIDs: string[];
  successLook: string;
  competitionLevel: string | null;
  /** Ambassador code accepted by validateAmbassadorCode, or "". */
  campaignCode: string;
  /** Merge into an existing users/{uid} instead of creating it. */
  existingProfile?: boolean;
}

/**
 * Mirrors the `UserData` payload in iOS `OnboardingController.finish`:
 * role Athlete, the free-text "what does success look like?" answer stored
 * as the athlete's goals text (`performanceGoals`, Events.md §2), every other
 * field default. `complete` is false while a paid plan waits on Stripe.
 *
 * isEmailVerified is not included. The wizard draft can claim a provider
 * vouched for the address, and the owner can write private/data, so the
 * client must not assert that flag. verifyEmailCode sets it server-side.
 */
export function buildRegistrationPayload(
  input: OnboardingRegistrationInput,
  complete: boolean = true,
): UserData {
  const goal = input.successLook.trim();
  const campaignCode = input.campaignCode.trim() || (safeGetItem("campaignCode")?.trim() ?? "");

  return {
    // private/data
    email: input.email,
    name: input.firstName.trim(),
    surname: "",
    dob: input.dob,
    gender: input.sex ?? "",
    country: "",
    // users/{uid}
    username: input.username.trim(),
    role: "Athlete",
    isCoach: false,
    trainingSportIDs: [...input.selectedSportIDs],
    cycleDays: 7,
    blockDays: 28,
    isImperial: false,
    onboardingComplete: complete,
    reason: "",
    simpleWellnessSetting: false,
    isPrivateProfile: false,
    deActivated: false,
    sleepGoal: 8,
    loadGoal: 400,
    weeklyLoadGoal: 2800,
    performanceGoals: goal,
    healthGoals: "",
    casualMode: false,
    disableInsights: false,
    autoPostTraining: false,
    hidePeakScore: false,
    hideWellnessSleep: false,
    ...(campaignCode && { campaignCode }),
  };
}

/**
 * Links the signed-in athlete to a coach organisation. The code is the one
 * from `assignInviteCode`; the callable rejects unknown, used, and expired codes.
 */
export async function redeemInviteCode(code: string): Promise<{ coachName?: string }> {
  const redeemFn = httpsCallable<{ code: string }, { success: boolean; coachName?: string }>(functions, "redeemInviteCode");
  const result = await redeemFn({ code: code.trim() });
  return { coachName: result.data?.coachName };
}

export type AmbassadorCodeResult =
  | { valid: true; campaignCode: string; ambassadorFirstName: string }
  | { valid: false; reason: "not_found" | "inactive" };

/** Checks an athlete ambassador code against the campaigns collection. */
export async function validateAmbassadorCode(code: string): Promise<AmbassadorCodeResult> {
  const validateFn = httpsCallable<{ code: string }, AmbassadorCodeResult>(functions, "validateAmbassadorCode");
  const result = await validateFn({ code: code.trim() });
  return result.data;
}

/**
 * Narrow update for a profile that already exists. A skipped goal is left
 * out so an empty string cannot replace goals the account already stored.
 */
export function buildExistingProfileUpdate(payload: UserData): Partial<UserData> {
  const update: Partial<UserData> = { onboardingComplete: payload.onboardingComplete === true };
  if (payload.trainingSportIDs && payload.trainingSportIDs.length > 0) {
    update.trainingSportIDs = payload.trainingSportIDs;
  }
  if (payload.performanceGoals) {
    update.performanceGoals = payload.performanceGoals;
  }
  if (payload.name) update.name = payload.name;
  if (payload.username) update.username = payload.username;
  if (payload.gender) update.gender = payload.gender;
  if (payload.dob) update.dob = payload.dob;
  if (payload.campaignCode) update.campaignCode = payload.campaignCode;
  return update;
}

/** Fields merged onto users/{uid} right after the profile write (iOS "extras"). */
export function buildExtrasPayload(input: OnboardingRegistrationInput, complete: boolean = true): Partial<UserData> {
  const extras: Partial<UserData> = {};
  if (input.competitionLevel) extras.competitionLevel = input.competitionLevel;
  if (!complete) extras.onboardingResumeStep = "plan";
  // The wearable, reminder and first wellness log steps run in the native app.
  extras.nativeSetupPending = true;
  return extras;
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export interface OnboardingStatusOptions {
  onStatus?: (status: string) => void;
  /** Test seam — skip the cosmetic pauses. */
  pauses?: boolean;
}

/**
 * Profile write → extras merge, from iOS `OnboardingController.finish`.
 * With `complete: false` (a paid plan about to open Stripe Checkout) the
 * profile is saved so the webhook can credit the ambassador, but onboarding
 * stays open until checkout succeeds. Only the profile write is fatal.
 * Returns true when the server already had a completed profile.
 */
export async function saveOnboardingProfile(
  input: OnboardingRegistrationInput,
  options: OnboardingStatusOptions & { complete: boolean },
): Promise<boolean> {
  const { complete, onStatus, pauses = true } = options;
  const status = (s: string) => onStatus?.(s);

  status(FINISH_STATUS.profile);
  const payload = buildRegistrationPayload(input, complete);
  // The wizard's existingProfile flag is localStorage state and can be stale.
  // Read the server doc before deciding to create. A failed read must not
  // fall through to a full replace.
  let serverProfile: UserData | null;
  try {
    serverProfile = await userService.getUserData(input.uid);
  } catch (error) {
    console.error("[Onboarding] could not read the existing profile:", error);
    throw new Error("Could not load your profile. Please try again.");
  }

  const alreadyComplete = serverProfile?.onboardingComplete === true;
  if (alreadyComplete) return true;

  if (serverProfile || input.existingProfile) {
    await userService.updateUserProfile(input.uid, buildExistingProfileUpdate(payload));
  } else {
    await userService.completeUserRegistration(input.uid, payload);
  }

  status(FINISH_STATUS.extras);
  const extras = buildExtrasPayload(input, complete);
  if (Object.keys(extras).length > 0) {
    try {
      await userService.updateUserProfile(input.uid, extras);
    } catch (error) {
      console.warn("[Onboarding] extras merge failed (non-fatal):", error);
    }
  }
  if (payload.campaignCode) userService.cleanupCampaignCode(payload.campaignCode);
  if (pauses) await sleep(450);
  return false;
}

/**
 * Marks onboarding complete and sends the welcome email. Runs once a plan is
 * chosen: straight away for Free, after Stripe returns for paid plans. The
 * welcome email is sent at most once per account (server-side guard).
 */
export async function markOnboardingComplete(
  uid: string,
  options: OnboardingStatusOptions & { alreadyComplete?: boolean } = {},
): Promise<void> {
  const { onStatus, pauses = true, alreadyComplete = false } = options;
  const status = (s: string) => onStatus?.(s);

  if (!alreadyComplete) {
    await userService.updateUserProfile(uid, { onboardingComplete: true });
  }

  status(FINISH_STATUS.email);
  try {
    const processNewUser = httpsCallable(functions, "processNewUserV2");
    await processNewUser({});
  } catch (error) {
    console.warn("[Onboarding] processNewUserV2 failed (non-fatal):", error);
  }

  status(FINISH_STATUS.dashboard);
  if (pauses) await sleep(600);
}

/** Free plan: write the completed profile and send the welcome email. */
export async function completeOnboarding(
  input: OnboardingRegistrationInput,
  options: OnboardingStatusOptions = {},
): Promise<void> {
  await saveOnboardingProfile(input, { ...options, complete: true });
  await markOnboardingComplete(input.uid, { ...options, alreadyComplete: true });
}

export interface PaidPlanSelection {
  type: "plus" | "premium";
  name: string;
  billingPeriod: "month" | "quarter" | "year";
}

/**
 * The paid onboarding path: flat USD pricing through the new (PEAK) Stripe
 * account — no region lookup, no pre-created customer (the checkout endpoint
 * creates one lazily for this account). 14-day trial unless `hasUsedTrial`.
 * Stripe returns to /onboarding so the flow can finish with the first
 * wellness log before the download page; the session is tagged so the
 * webhook can also complete it.
 */
export async function createPlanCheckoutUrl(params: {
  uid: string;
  email: string;
  plan: PaidPlanSelection;
  validatedCountry: string;
  stripeCustomerId?: string | null;
  hasUsedTrial?: boolean;
  origin: string;
}): Promise<string | null> {
  const { uid, plan, hasUsedTrial, origin } = params;

  const priceId = StripeService.getPeakPriceId(
    plan.type,
    plan.billingPeriod === "year" ? "year" : "month",
  );

  const successUrl = `${origin}/onboarding?subscription_success=true`;
  const cancelUrl = `${origin}/onboarding?subscription_canceled=true`;
  const trialDays = hasUsedTrial ? 0 : 14;

  return StripeService.createCheckoutSession(
    priceId,
    "",
    successUrl,
    cancelUrl,
    uid,
    plan.name,
    trialDays,
    undefined,
    "onboarding",
  );
}
